/**
 * MODULE: domain/sales/frontend/components/accounts/InteractiveOrgChart.tsx
 * PURPOSE: Interactive org chart with draggable cards, connection lines, pan/zoom, export
 * DEPENDENCIES: react-zoom-pan-pinch, flowbite-react, react-icons, react-router-dom, html-to-image
 * PUBLIC API: InteractiveOrgChart (default), InteractiveOrgChartRef, AddContactPrefill
 * SIDE EFFECTS: localStorage (positions, drafts), html-to-image export
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~1150 / 1200 lines
 * RECENT: 2026-07-07 [2.2.1521] - Risk-aware nodes: role/strength/sentiment badges + "gone quiet" alert + risk border (StakeholderRiskBadges, getRiskBorderClass).
 * RECENT_PRIOR: 2026-02-15 - Theme (light/dark); UX (undo/redo, draft counter, connection hover); grid removed; forwardRef openQuickAdd
 */

import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { Button, Badge, Tooltip } from 'flowbite-react';
import {
  HiZoomIn,
  HiZoomOut,
  HiArrowsExpand,
  HiRefresh,
  HiOutlinePencil,
  HiCamera,
  HiPlus,
  HiUserAdd,
  HiOfficeBuilding,
  HiArrowRight,
  HiArrowLeft,
} from 'react-icons/hi';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { QuickAddCardForm } from './QuickAddCardForm';
import { InlineCardEditor } from './InlineCardEditor';
import { CardType, type OrgChartNodeData } from './OrgChartNode';
import { StakeholderRiskBadges } from './StakeholderRiskBadges';
import { getRiskBorderClass } from './stakeholderRisk';

interface CardPosition {
  id: string;
  x: number;
  y: number;
}

/** Prefill for Add Contact modal when converting placeholder to contact */
export interface AddContactPrefill {
  first_name: string;
  last_name: string;
  job_title?: string;
  reports_to?: string;
}

interface InteractiveOrgChartProps {
  rootNode: OrgChartNodeData;
  onEdit: (contact: OrgChartNodeData) => void;
  /** Called when converting placeholder to contact or adding a new contact; prefill opens modal with data */
  onAddContact?: (prefill?: AddContactPrefill) => void;
  /** Called when updating reports_to (connection drag or add-between) */
  onUpdateReportsTo?: (fromCardId: string, toCardId: string) => void;
  /** Called when updating card data (inline edit save) - for real contacts */
  onUpdateCard?: (nodeId: string, updates: Partial<OrgChartNodeData>) => void;
  /** Called when temp cards change - for draft counter badge in parent */
  onTempCardsChange?: (payload: { rootId: string; count: number; saveAllDrafts: () => void }) => void;
  className?: string;
}

interface HistoryState {
  positions: CardPosition[];
  tempCards: OrgChartNodeData[];
  timestamp: number;
}

export interface InteractiveOrgChartRef {
  openQuickAdd: (position?: { x: number; y: number }) => void;
}

const CARD_WIDTH = 240;
const CARD_HEIGHT = 180;
const HORIZONTAL_GAP = 120;
const VERTICAL_GAP = 140;
const CANVAS_PADDING = 200;

function getInitials(firstName?: string, lastName?: string): string {
  return `${(firstName?.[0] ?? '').toUpperCase()}${(lastName?.[0] ?? '').toUpperCase()}`;
}

const AVATAR_COLORS = [
  'bg-blue-600',
  'bg-purple-600',
  'bg-green-600',
  'bg-yellow-600',
  'bg-pink-600',
  'bg-indigo-600',
  'bg-red-600',
  'bg-teal-600',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function calculateInitialPositions(
  node: OrgChartNodeData,
  level: number = 0,
  positions: CardPosition[] = [],
  siblingIndex: number = 0,
  siblingCount: number = 1,
  parentX: number = 0
): CardPosition[] {
  let x: number;
  if (level === 0) {
    x = CANVAS_PADDING;
  } else if (siblingCount === 1) {
    x = parentX;
  } else {
    const totalWidth = siblingCount * CARD_WIDTH + (siblingCount - 1) * HORIZONTAL_GAP;
    const startX = parentX - totalWidth / 2 + CARD_WIDTH / 2;
    x = startX + siblingIndex * (CARD_WIDTH + HORIZONTAL_GAP);
  }

  const y = CANVAS_PADDING + level * (CARD_HEIGHT + VERTICAL_GAP);
  positions.push({ id: node.id, x, y });

  if (node.children && node.children.length > 0) {
    const childrenCenterX = x + CARD_WIDTH / 2;
    node.children.forEach((child, index) => {
      calculateInitialPositions(
        child,
        level + 1,
        positions,
        index,
        node.children!.length,
        childrenCenterX
      );
    });
  }

  return positions;
}

function calculateCanvasBounds(positions: CardPosition[]): { width: number; height: number } {
  if (positions.length === 0) return { width: 1200, height: 800 };

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  positions.forEach((pos) => {
    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x + CARD_WIDTH);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y + CARD_HEIGHT);
  });

  const width = Math.max(1200, maxX - minX + CANVAS_PADDING * 2);
  const height = Math.max(800, maxY - minY + CANVAS_PADDING * 2);

  return { width, height };
}

/** Calculate optimal position for new card between parent and child */
function calculateInsertPosition(
  parentPos: CardPosition,
  childPos: CardPosition
): { x: number; y: number } {
  return {
    x: (parentPos.x + childPos.x) / 2,
    y: (parentPos.y + childPos.y) / 2,
  };
}

function findConnections(node: OrgChartNodeData, connections: { from: string; to: string }[] = []): { from: string; to: string }[] {
  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => {
      connections.push({ from: node.id, to: child.id });
      findConnections(child, connections);
    });
  }
  return connections;
}

function flattenTree(node: OrgChartNodeData, nodes: OrgChartNodeData[] = []): OrgChartNodeData[] {
  nodes.push(node);
  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => flattenTree(child, nodes));
  }
  return nodes;
}

const InteractiveOrgChartInner = forwardRef<InteractiveOrgChartRef, InteractiveOrgChartProps>(
  function InteractiveOrgChart(
    {
      rootNode,
      onEdit,
      onAddContact,
      onUpdateReportsTo,
      onUpdateCard,
      onTempCardsChange,
      className = '',
    },
    ref
  ) {
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef<number>(1);
  const [positions, setPositions] = useState<CardPosition[]>(() =>
    calculateInitialPositions(rootNode)
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ clientX: 0, clientY: 0, cardX: 0, cardY: 0 });
  const [connectionHoverTarget, setConnectionHoverTarget] = useState<string | null>(null);
  const [quickAddMode, setQuickAddMode] = useState<{
    parentId: string | null;
    position: { x: number; y: number } | null;
  }>({ parentId: null, position: null });
  const [tempCards, setTempCards] = useState<OrgChartNodeData[]>([]);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [connectMode, setConnectMode] = useState<{
    fromCardId: string | null;
    fromCardScreenPos: { x: number; y: number } | null;
    cursorPos: { x: number; y: number } | null;
  }>({ fromCardId: null, fromCardScreenPos: null, cursorPos: null });
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const allNodes = flattenTree(rootNode);
  const allNodesWithTemp = [...allNodes, ...tempCards];
  const connections = [
    ...findConnections(rootNode),
    ...tempCards
      .filter((c) => c.reports_to)
      .map((c) => ({ from: c.reports_to!, to: c.id })),
  ];
  const canvasBounds = calculateCanvasBounds(positions);

  useEffect(() => {
    setPositions(calculateInitialPositions(rootNode));
  }, [rootNode.id]);

  const saveToHistory = useCallback(() => {
    const newState: HistoryState = {
      positions: positions.map((p) => ({ ...p })),
      tempCards: tempCards.map((c) => ({ ...c })),
      timestamp: Date.now(),
    };
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newState);
      if (newHistory.length > 50) newHistory.shift();
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [positions, tempCards, historyIndex]);

  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    const prevState = history[historyIndex - 1];
    setPositions(prevState.positions.map((p) => ({ ...p })));
    setTempCards(prevState.tempCards.map((c) => ({ ...c })));
    setHistoryIndex((prev) => prev - 1);
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const nextState = history[historyIndex + 1];
    setPositions(nextState.positions.map((p) => ({ ...p })));
    setTempCards(nextState.tempCards.map((c) => ({ ...c })));
    setHistoryIndex((prev) => prev + 1);
  }, [history, historyIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  const handleSaveAllDrafts = useCallback(() => {
    const idsToRemove = new Set(tempCards.map((t) => t.id));
    tempCards.forEach((card) => {
      if (onAddContact) {
        onAddContact({
          first_name: card.first_name,
          last_name: card.last_name,
          job_title: card.job_title ?? undefined,
          reports_to: card.reports_to ?? undefined,
        });
      }
    });
    setTempCards([]);
    setPositions((prev) => prev.filter((p) => !idsToRemove.has(p.id)));
    const key = `org-chart-drafts-${rootNode.id}`;
    localStorage.removeItem(key);
  }, [tempCards, onAddContact, rootNode.id]);

  useEffect(() => {
    onTempCardsChange?.({
      rootId: rootNode.id,
      count: tempCards.length,
      saveAllDrafts: handleSaveAllDrafts,
    });
  }, [tempCards.length, rootNode.id, onTempCardsChange, handleSaveAllDrafts]);

  // Load saved positions from localStorage
  useEffect(() => {
    const key = `org-chart-positions-${rootNode.id}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as CardPosition[];
        const valid = parsed.every((p) => typeof p.id === 'string' && typeof p.x === 'number' && typeof p.y === 'number');
        if (valid && parsed.length === allNodes.length) {
          setPositions(parsed);
        }
      } catch {
        // Use calculated positions if saved data is invalid
      }
    }
  }, [rootNode.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save positions to localStorage (only real nodes; temp cards are ephemeral)
  useEffect(() => {
    const key = `org-chart-positions-${rootNode.id}`;
    const realPositions = positions.filter((p) => allNodes.some((n) => n.id === p.id));
    localStorage.setItem(key, JSON.stringify(realPositions));
  }, [positions, rootNode.id, allNodes]);

  // Save temp cards to localStorage
  useEffect(() => {
    if (tempCards.length > 0) {
      const key = `org-chart-drafts-${rootNode.id}`;
      localStorage.setItem(key, JSON.stringify(tempCards));
    }
  }, [tempCards, rootNode.id]);

  // Load temp cards from localStorage on mount
  useEffect(() => {
    const key = `org-chart-drafts-${rootNode.id}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const drafts = JSON.parse(saved) as OrgChartNodeData[];
        setTempCards(drafts);
        setPositions((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newPositions = drafts
            .filter((c) => !existingIds.has(c.id))
            .map((c) => ({ id: c.id, x: 400, y: 400 }));
          return newPositions.length > 0 ? [...prev, ...newPositions] : prev;
        });
      } catch {
        console.warn('Failed to load draft cards');
      }
    }
  }, [rootNode.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    openQuickAdd: (position?: { x: number; y: number }) => {
      setQuickAddMode({
        parentId: rootNode.id,
        position: position ?? { x: 400, y: 400 },
      });
    },
  }));

  const handleZoomIn = () => transformRef.current?.zoomIn();
  const handleZoomOut = () => transformRef.current?.zoomOut();
  const handleReset = () => {
    transformRef.current?.resetTransform();
    setPositions(calculateInitialPositions(rootNode));
  };
  const handleFitToScreen = () => transformRef.current?.centerView(1, 300);

  const handleExport = useCallback(async () => {
    if (!canvasRef.current) return;
    try {
      const isDark = document.documentElement.classList.contains('dark');
      const dataUrl = await toPng(canvasRef.current, {
        backgroundColor: isDark ? '#111827' : '#f9fafb',
        pixelRatio: 2,
      });
      const link = document.createElement('a');
      link.download = `org-chart-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    }
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, cardId: string) => {
      if ((e.target as HTMLElement).closest('button, a')) return;

      const card = positions.find((p) => p.id === cardId);
      if (!card) return;

      setDraggingId(cardId);
      setDragStart({
        clientX: e.clientX,
        clientY: e.clientY,
        cardX: card.x,
        cardY: card.y,
      });
      e.preventDefault();
      e.stopPropagation();
    },
    [positions]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingId) return;

      const scale = scaleRef.current ?? 1;
      const deltaX = (e.clientX - dragStart.clientX) / scale;
      const deltaY = (e.clientY - dragStart.clientY) / scale;

      setPositions((prev) =>
        prev.map((p) =>
          p.id === draggingId
            ? { ...p, x: dragStart.cardX + deltaX, y: dragStart.cardY + deltaY }
            : p
        )
      );
    },
    [draggingId, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    if (draggingId) saveToHistory();
    setDraggingId(null);
  }, [draggingId, saveToHistory]);

  useEffect(() => {
    if (draggingId) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingId, handleMouseMove, handleMouseUp]);

  const handleTransformed = useCallback((_ref: ReactZoomPanPinchRef, state: { scale: number }) => {
    scaleRef.current = state.scale;
  }, []);

  const handleAddCard = useCallback(
    (parentId: string, _type: CardType) => {
      const parentPos = positions.find((p) => p.id === parentId);
      if (!parentPos) return;
      const newX = parentPos.x;
      const newY = parentPos.y + CARD_HEIGHT + VERTICAL_GAP;
      setQuickAddMode({ parentId, position: { x: newX, y: newY } });
    },
    [positions]
  );

  const handleSaveQuickCard = useCallback(
    (cardData: {
      firstName: string;
      lastName: string;
      title?: string;
      cardType: CardType;
      groupType?: string;
    }) => {
      const tempId = `temp-${Date.now()}`;
      const { parentId, position } = quickAddMode;
      if (!position || !parentId) return;

      const newCard: OrgChartNodeData = {
        id: tempId,
        first_name: cardData.firstName,
        last_name: cardData.cardType === CardType.GROUP ? '' : cardData.lastName,
        job_title: cardData.title ?? null,
        email: '',
        account_id: '',
        is_primary_contact: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        cardType: cardData.cardType,
        groupType: cardData.groupType as OrgChartNodeData['groupType'],
        isPlaceholder: cardData.cardType === CardType.PLACEHOLDER,
        createdInOrgChart: true,
        reports_to: parentId,
        children: [],
      } as OrgChartNodeData;

      setTempCards((prev) => [...prev, newCard]);
      setPositions((prev) => [...prev, { id: tempId, x: position.x, y: position.y }]);
      setQuickAddMode({ parentId: null, position: null });
      saveToHistory();
    },
    [quickAddMode, saveToHistory]
  );

  const handleConvertToContact = useCallback(
    (tempCard: OrgChartNodeData) => {
      if (!onAddContact) return;
      onAddContact({
        first_name: tempCard.first_name,
        last_name: tempCard.last_name,
        job_title: tempCard.job_title ?? undefined,
        reports_to: tempCard.reports_to ?? undefined,
      });
      setTempCards((prev) => prev.filter((c) => c.id !== tempCard.id));
      setPositions((prev) => prev.filter((p) => p.id !== tempCard.id));
    },
    [onAddContact]
  );

  const handleConnectionStart = useCallback((e: React.MouseEvent, fromCardId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const cardEl = (e.target as HTMLElement).closest('[data-card-id]') as HTMLElement | null;
    const cardRect = cardEl?.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!cardRect || !containerRect) return;
    setConnectMode({
      fromCardId,
      fromCardScreenPos: {
        x: cardRect.right - containerRect.left - 12,
        y: cardRect.top - containerRect.top + cardRect.height / 2,
      },
      cursorPos: {
        x: e.clientX - containerRect.left,
        y: e.clientY - containerRect.top,
      },
    });
  }, []);

  useEffect(() => {
    if (!connectMode.fromCardId) return;

    const handleMouseMove = (e: MouseEvent) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      setConnectMode((prev) =>
        prev
          ? {
              ...prev,
              cursorPos: {
                x: e.clientX - containerRect.left,
                y: e.clientY - containerRect.top,
              },
            }
          : prev
      );
    };

    const handleMouseUp = () => {
      setConnectMode({
        fromCardId: null,
        fromCardScreenPos: null,
        cursorPos: null,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [connectMode?.fromCardId]);

  const handleConnectionDrop = useCallback(
    (toCardId: string) => {
      if (!connectMode.fromCardId) return;
      if (connectMode.fromCardId === toCardId) {
        setConnectMode({ fromCardId: null, fromCardScreenPos: null, cursorPos: null });
        return;
      }
      onUpdateReportsTo?.(connectMode.fromCardId, toCardId);
      setConnectMode({ fromCardId: null, fromCardScreenPos: null, cursorPos: null });
    },
    [connectMode?.fromCardId, onUpdateReportsTo]
  );

  const handleAddBetween = useCallback(
    (fromId: string, toId: string, position?: { x: number; y: number }) => {
      const fromPos = positions.find((p) => p.id === fromId);
      const toPos = positions.find((p) => p.id === toId);
      const insertPos =
        fromPos && toPos
          ? calculateInsertPosition(fromPos, toPos)
          : position ?? { x: 400, y: 400 };

      const newCard: OrgChartNodeData = {
        id: `temp-${Date.now()}`,
        first_name: '',
        last_name: '',
        job_title: '',
        email: '',
        account_id: '',
        is_primary_contact: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        cardType: CardType.PLACEHOLDER,
        isPlaceholder: true,
        createdInOrgChart: true,
        reports_to: fromId,
        children: [],
      } as OrgChartNodeData;

      setTempCards((prev) => [...prev, newCard]);
      setPositions((prev) => [...prev, { id: newCard.id, x: insertPos.x, y: insertPos.y }]);
      setEditingCardId(newCard.id);
      saveToHistory();

      const toIsTemp = tempCards.some((c) => c.id === toId);
      if (toIsTemp) {
        setTempCards((prev) =>
          prev.map((c) => (c.id === toId ? { ...c, reports_to: newCard.id } : c))
        );
      } else {
        onUpdateReportsTo?.(toId, newCard.id);
      }
    },
    [tempCards, onUpdateReportsTo, positions, saveToHistory]
  );

  const handleUpdateCard = useCallback(
    (updated: Partial<OrgChartNodeData>) => {
      const nodeId = editingCardId;
      if (!nodeId) return;

      const isTemp = tempCards.some((c) => c.id === nodeId);
      if (isTemp) {
        setTempCards((prev) =>
          prev.map((c) => (c.id === nodeId ? { ...c, ...updated } : c))
        );
      } else {
        const apiUpdates: { first_name?: string; last_name?: string; job_title?: string } = {};
        if (updated.first_name !== undefined) apiUpdates.first_name = updated.first_name;
        if (updated.last_name !== undefined) apiUpdates.last_name = updated.last_name;
        if (updated.job_title !== undefined) {
          apiUpdates.job_title = updated.job_title ?? undefined;
        }
        if (Object.keys(apiUpdates).length > 0) onUpdateCard?.(nodeId, apiUpdates);
      }
      setEditingCardId(null);
    },
    [editingCardId, tempCards, onUpdateCard]
  );

  const handleDeleteCard = useCallback(
    (nodeId: string) => {
      setTempCards((prev) => prev.filter((c) => c.id !== nodeId));
      setPositions((prev) => prev.filter((p) => p.id !== nodeId));
      setEditingCardId(null);
    },
    []
  );

  return (
    <div ref={containerRef} className={`relative w-full h-full bg-gray-50 dark:bg-gray-900 ${className}`}>
      <style>{`
        .connection-group:hover .connection-line {
          stroke: #3b82f6;
          stroke-width: 3;
        }
        .dark .connection-group:hover .connection-line {
          stroke: #60a5fa;
          stroke-width: 3;
        }
      `}</style>
      {/* Zoom Controls Toolbar - Flowbite Pro styling */}
      <div className="absolute top-4 right-4 z-20 flex gap-2 bg-white dark:bg-gray-800 rounded-lg p-2 shadow-lg border border-gray-200 dark:border-gray-700">
        <Tooltip content="Undo (Cmd+Z)">
          <Button
            size="sm"
            color="gray"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
          >
            <HiArrowLeft className="h-4 w-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Redo (Cmd+Shift+Z)">
          <Button
            size="sm"
            color="gray"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1 || history.length === 0}
          >
            <HiArrowRight className="h-4 w-4" />
          </Button>
        </Tooltip>
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
        <Tooltip content="Zoom In">
          <Button size="sm" color="gray" onClick={handleZoomIn}>
            <HiZoomIn className="h-4 w-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Zoom Out">
          <Button size="sm" color="gray" onClick={handleZoomOut}>
            <HiZoomOut className="h-4 w-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Fit to Screen">
          <Button size="sm" color="gray" onClick={handleFitToScreen}>
            <HiArrowsExpand className="h-4 w-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Reset Layout">
          <Button size="sm" color="gray" onClick={handleReset}>
            <HiRefresh className="h-4 w-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Export as Image">
          <Button size="sm" color="gray" onClick={handleExport}>
            <HiCamera className="h-4 w-4" />
          </Button>
        </Tooltip>
      </div>

      {/* Instructions Badge */}
      <div className="absolute top-4 left-4 z-20 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 shadow-lg">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          <span className="font-medium text-blue-600 dark:text-blue-400">Click card</span> to edit •
          <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">Drag blue dot</span> to connect •
          <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">Click +</span> on line to insert •
          <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">Scroll</span> to zoom
        </p>
      </div>

      {/* Temporary connection line overlay - screen coords during drag */}
      {connectMode.fromCardId &&
        connectMode.fromCardScreenPos &&
        connectMode.cursorPos && (
          <div
            className="pointer-events-none absolute inset-0 z-30"
            aria-hidden
          >
            <svg
              className="absolute inset-0 h-full w-full"
              style={{ overflow: 'visible' }}
            >
              <defs>
                <marker
                  id="arrowhead-overlay"
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3, 0 6" fill="#3B82F6" />
                </marker>
              </defs>
              <line
                x1={connectMode.fromCardScreenPos.x}
                y1={connectMode.fromCardScreenPos.y}
                x2={connectMode.cursorPos.x}
                y2={connectMode.cursorPos.y}
                stroke="#3B82F6"
                strokeWidth="3"
                strokeDasharray="8,4"
                markerEnd="url(#arrowhead-overlay)"
              />
            </svg>
          </div>
        )}

      {/* Pan/Zoom Canvas */}
      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={0.3}
        maxScale={3}
        centerOnInit
        limitToBounds={false}
        panning={{ disabled: !!draggingId }}
        wheel={{ step: 0.1 }}
        doubleClick={{ disabled: false, mode: 'zoomIn', step: 0.3 }}
        onTransformed={handleTransformed}
      >
        <TransformComponent
          wrapperClass="!w-full !h-full"
          contentClass="!w-full !h-full"
          contentStyle={{ overflow: 'visible' }}
        >
          <div
            ref={canvasRef}
            className="relative bg-gray-50 dark:bg-gray-900"
            style={{
              width: canvasBounds.width,
              height: canvasBounds.height,
              minWidth: canvasBounds.width,
              minHeight: canvasBounds.height,
            }}
          >
            {/* SVG Layer - Connection lines (pointer-events on add buttons only) */}
            <svg
              className="absolute top-0 left-0"
              style={{
                width: '100%',
                height: '100%',
                overflow: 'visible',
              }}
            >
              <defs>
                <marker
                  id="arrowhead-light"
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3, 0 6" fill="#9CA3AF" />
                </marker>
                <marker
                  id="arrowhead-dark"
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3, 0 6" fill="#6B7280" />
                </marker>
              </defs>
              {connections.map(({ from, to }) => {
                const fromPosition = positions.find((p) => p.id === from);
                const toPosition = positions.find((p) => p.id === to);

                if (!fromPosition || !toPosition) return null;

                const x1 = fromPosition.x + CARD_WIDTH / 2;
                const y1 = fromPosition.y + CARD_HEIGHT - 20;
                const x2 = toPosition.x + CARD_WIDTH / 2;
                const y2 = toPosition.y;
                const midX = (x1 + x2) / 2;
                const midY = (y1 + y2) / 2;
                const path = `M ${x1},${y1} C ${x1},${midY} ${x2},${midY} ${x2},${y2}`;

                return (
                  <g
                    key={`${from}-${to}`}
                    className="connection-group"
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  >
                    <path
                      d={path}
                      stroke="#9CA3AF"
                      strokeWidth="2"
                      fill="none"
                      markerEnd="url(#arrowhead-light)"
                      className="connection-line transition-all duration-200 dark:hidden"
                    />
                    <path
                      d={path}
                      stroke="#6B7280"
                      strokeWidth="2"
                      fill="none"
                      markerEnd="url(#arrowhead-dark)"
                      className="connection-line transition-all duration-200 hidden dark:block"
                    />
                    <path
                      d={path}
                      stroke="transparent"
                      strokeWidth="12"
                      fill="none"
                      style={{ pointerEvents: 'auto' }}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Add buttons on connection lines - HTML overlay for clickability */}
            {connections.map(({ from, to }) => {
              const fromPosition = positions.find((p) => p.id === from);
              const toPosition = positions.find((p) => p.id === to);
              if (!fromPosition || !toPosition) return null;

              const x1 = fromPosition.x + CARD_WIDTH / 2;
              const y1 = fromPosition.y + CARD_HEIGHT - 20;
              const x2 = toPosition.x + CARD_WIDTH / 2;
              const y2 = toPosition.y;
              const midX = (x1 + x2) / 2;
              const midY = (y1 + y2) / 2;

              return (
                <button
                  key={`add-${from}-${to}`}
                  type="button"
                  className="absolute z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-800 text-blue-600 dark:text-white text-base font-bold shadow-lg transition-colors hover:bg-blue-500 dark:hover:bg-blue-600"
                  style={{
                    left: midX - 16,
                    top: midY - 16,
                    pointerEvents: 'auto',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddBetween(from, to);
                  }}
                >
                  +
                </button>
              );
            })}

            {/* Cards Layer */}
            {allNodesWithTemp.map((node) => {
              const position = positions.find((p) => p.id === node.id);
              if (!position) return null;

              const isGroup = node.cardType === CardType.GROUP;
              const fullName = isGroup
                ? (node.first_name ?? '').trim()
                : `${node.first_name ?? ''} ${node.last_name ?? ''}`.trim();
              const initials = getInitials(node.first_name, node.last_name);
              const avatarColor = getAvatarColor(fullName);
              const isRoot = node.id === rootNode.id;
              const childCount =
                (node.children?.length ?? 0) +
                tempCards.filter((t) => t.reports_to === node.id).length;
              const hasChildren = childCount > 0;
              const isPlaceholder =
                node.cardType === CardType.PLACEHOLDER || node.createdInOrgChart;
              const isTempCard = tempCards.some((c) => c.id === node.id);
              const canConvert = isTempCard && (isPlaceholder || node.cardType === CardType.CONTACT);
              const isEditing = editingCardId === node.id;
              const isDropTarget =
                connectMode.fromCardId &&
                connectMode.fromCardId !== node.id;

              const cardBorderClass = isGroup
                ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30 hover:border-purple-600 dark:hover:border-purple-400'
                : `${getRiskBorderClass(node, 'border-gray-300 dark:border-gray-600')} bg-white dark:bg-gray-800 hover:border-blue-500 dark:hover:border-blue-400`;

              return (
                <div
                  key={node.id}
                  data-card-id={node.id}
                  className={`absolute transition-all duration-200 ${
                    draggingId === node.id ? 'cursor-grabbing shadow-2xl z-50' : 'cursor-grab z-10'
                  } ${
                    connectionHoverTarget === node.id
                      ? 'ring-4 ring-blue-500 ring-opacity-50 scale-105'
                      : ''
                  } ${
                    connectMode.fromCardId === node.id ? 'ring-2 ring-blue-400' : ''
                  }`}
                  style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    width: `${CARD_WIDTH}px`,
                    pointerEvents: 'auto',
                  }}
                  onMouseEnter={() => {
                    if (connectMode.fromCardId && connectMode.fromCardId !== node.id) {
                      setConnectionHoverTarget(node.id);
                    }
                  }}
                  onMouseLeave={() => {
                    setConnectionHoverTarget(null);
                  }}
                  onMouseDown={(e) => {
                    if (!isEditing && !(e.target as HTMLElement).closest('button[data-connection-handle]'))
                      handleMouseDown(e, node.id);
                  }}
                  onClick={(e) => {
                    if (!isEditing && !draggingId && !(e.target as HTMLElement).closest('button, a')) {
                      e.stopPropagation();
                      setEditingCardId(node.id);
                    }
                  }}
                  onMouseUp={() => {
                    if (connectMode.fromCardId && connectMode.fromCardId !== node.id) {
                      handleConnectionDrop(node.id);
                    }
                  }}
                >
                  {isEditing ? (
                    <InlineCardEditor
                      node={node}
                      onSave={(updated) => handleUpdateCard(updated)}
                      onCancel={() => setEditingCardId(null)}
                      onDelete={() => handleDeleteCard(node.id)}
                    />
                  ) : (
                    <div className="relative group">
                      <div
                        className={`rounded-lg border-2 p-4 shadow-md transition-all duration-200 hover:shadow-xl ${
                          isDropTarget ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-900' : ''
                        } ${cardBorderClass}`}
                      >
                        {/* Connection Handle - blue dot */}
                        <button
                          type="button"
                          data-connection-handle
                          className="absolute -right-3 top-1/2 z-20 -translate-y-1/2 cursor-pointer opacity-0 transition-opacity group-hover:opacity-100"
                          onMouseDown={(e) => handleConnectionStart(e, node.id)}
                        >
                          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-blue-400 dark:border-blue-300 bg-blue-600 dark:bg-blue-500 shadow-lg transition-transform hover:scale-110">
                            <HiArrowRight className="h-3 w-3 text-white" />
                          </div>
                        </button>

                        {/* Type Badge */}
                        <div className="absolute left-2 top-2 flex gap-1">
                          {isGroup && (
                            <Badge color="purple" size="xs">
                              {node.groupType || 'Business Unit'}
                            </Badge>
                          )}
                          {isPlaceholder && !isGroup && (
                            <Badge color="warning" size="xs">
                              Draft
                            </Badge>
                          )}
                        </div>

                        {/* Quick Actions - hover menu */}
                        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          {canConvert && onAddContact && (
                            <Tooltip content="Convert to Contact">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConvertToContact(node);
                                }}
                                className="rounded-md bg-blue-600 p-1.5 text-white hover:bg-blue-500"
                              >
                                <HiUserAdd className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          )}
                          {!isGroup && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(node);
                              }}
                              className="rounded-md p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                              title="Edit reporting line"
                            >
                              <HiOutlinePencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <Tooltip content="Add card below">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddCard(node.id, CardType.PLACEHOLDER);
                              }}
                              className="rounded-md p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                            >
                              <HiPlus className="h-3.5 w-3.5" />
                            </button>
                          </Tooltip>
                        </div>

                        {/* Avatar & Name - NO EMAIL */}
                        <div className="mb-2 flex items-center gap-3">
                          {isGroup ? (
                            <div className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-600">
                              <HiOfficeBuilding className="h-5 w-5 text-white" />
                            </div>
                          ) : (
                            <div
                              className={`relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${avatarColor}`}
                            >
                              <span className="text-sm font-medium text-white">{initials}</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                              {fullName || 'Unnamed'}
                            </h3>
                            {node.job_title && (
                              <p className="truncate text-xs text-gray-600 dark:text-gray-400">{node.job_title}</p>
                            )}
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex items-center justify-between">
                          {isRoot && !node.reports_to && (
                            <Badge color="purple" size="xs">
                              Top
                            </Badge>
                          )}
                          {hasChildren && (
                            <Badge color="gray" size="xs">
                              {childCount} report{childCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>

                        {/* Risk-aware stakeholder badges - contacts only */}
                        {!isGroup && <StakeholderRiskBadges contact={node} />}

                        {/* View in Pipeline - contacts only */}
                        {!isGroup && (
                          <div className="mt-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                            <Link
                              to={`/pipeline-generation?lead=${node.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
                            >
                              View in Pipeline
                              <HiArrowTopRightOnSquare className="h-3 w-3" />
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Quick Add Form */}
            {quickAddMode.position && (
              <QuickAddCardForm
                position={quickAddMode.position}
                onSave={handleSaveQuickCard}
                onCancel={() => setQuickAddMode({ parentId: null, position: null })}
              />
            )}
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
});

export { InteractiveOrgChartInner as InteractiveOrgChart };
export default InteractiveOrgChartInner;
